import { Fragment, useState } from 'react';

import { Badge, Divider, Modal, Text, TextButton } from '@thefarmersfront/kitchen';

import { ModalLabelValueRow, ModalSection, ProgressCount, Table } from '~/components/common';
import { ProgressBar } from '~/components/common/ProgressBar';

import { useModal } from '~/hooks/common';
import { useGetReportHistoriesByReportIdMutation } from '~/hooks/receive';
import { useUserInfoStore } from '~/store';
import { formatReceiveExpectedDateTime } from '~/utils';
import { getReceiveStatusDetail3PLColumnDefs } from '~/utils/receive/table';

import {
  RECEIVE_REPORT_STATUS,
  RECEIVE_REPORT_STATUS_BADGE_COLOR,
  RECEIVE_STATUS_STATUS,
  RECEIVE_STATUS_STATUS_BADGE_COLOR,
  RECEIVE_TOOLTIP_MESSAGE,
} from '~/constants';

import { ReceiveReportDetailModal } from '../../report';

import type { Type3PLReceiveStatusDetail, TypeReceiveReportHistoryDetail } from '~/types/receive.types';

import styles from './ReceiveStatusDetailModal3PL.module.scss';

const ReceiveStatusDetailModal3PL = ({
  open,
  data,
  onClose,
}: {
  open: boolean;
  data: Type3PLReceiveStatusDetail;
  onClose: () => void;
}) => {
  const { summary, progressCount, reportProgressCount, items } = data;

  const ownerCode = useUserInfoStore((state) => state.userInfo.ownerCode);

  const [reportDetailData, setReportDetailData] = useState<TypeReceiveReportHistoryDetail | null>(null);

  const reportDetailModal = useModal();

  const reportDetailMutation = useGetReportHistoriesByReportIdMutation();

  const { columnDefs } = getReceiveStatusDetail3PLColumnDefs();

  const handleViewReportDetail = (reportId: number) => {
    reportDetailMutation.mutate(
      { ownerCode, reportId: String(reportId) },
      {
        onSuccess: ({ data: detailData }) => {
          setReportDetailData(detailData);
          reportDetailModal.handleOpenModal();
        },
      }
    );
  };

  return (
    <Modal
      title={
        <div className={styles['title-wrapper']}>
          <Text className={styles['title']} size="xxlarge" weight="semi-bold">
            입고 현황 상세
          </Text>
          <Badge variant="light" status color={RECEIVE_STATUS_STATUS_BADGE_COLOR[summary?.status]}>
            {RECEIVE_STATUS_STATUS[summary?.status]}
          </Badge>
        </div>
      }
      size="xlarge"
      open={open}
      onConfirm={onClose}
    >
      <div className={styles['contents-container']}>
        <ModalSection className={styles['request-info']} title="요청 정보">
          <ModalLabelValueRow label="입고요청번호" value={summary.requestCode} />
          <ModalLabelValueRow label="요청등록일" value={summary.requestDate} />
          <ModalLabelValueRow
            label="입고예정일시"
            value={formatReceiveExpectedDateTime(summary.estimatedArrivalDate, summary.estimatedArrivalTime)}
          />
          {summary.xdock === '이용함' && (
            <ModalLabelValueRow
              label="최종입고예정일"
              value={summary.finalEstimatedArrivalDate}
              tooltip={{ description: RECEIVE_TOOLTIP_MESSAGE.FINAL_RECEIVE_EXPECTED_DATE, maxWidth: '201px' }}
            />
          )}
          <ModalLabelValueRow
            label="입고 센터"
            value={`${summary.receivingClusterName} / ${summary.receivingWarehouseName}`}
          />
          <ModalLabelValueRow
            label="공급사"
            value={
              <div className={styles['supplier-value']}>
                <Text size="large">{summary.supplierName}</Text>
                <Text size="large" color="quaternary">
                  {summary.supplierCode}
                </Text>
              </div>
            }
          />
          <ModalLabelValueRow label="입고 방법" value={summary.releaseCodeName} />
          <ModalLabelValueRow label="입고대행서비스" value={summary.xdock} />
          <ModalLabelValueRow
            label="리포트 상태"
            tooltip={{
              description: RECEIVE_TOOLTIP_MESSAGE.REPORT_STATUS_DESCRIPTION,
              maxWidth: '258px',
            }}
            value={
              <Badge
                className={styles['badge-value']}
                variant="light"
                color={RECEIVE_REPORT_STATUS_BADGE_COLOR[summary.reportStatus]}
              >
                {RECEIVE_REPORT_STATUS[summary.reportStatus].text}
              </Badge>
            }
          />
          <ModalLabelValueRow
            label="리포트 ID"
            alignItems="start"
            value={
              summary.reports.length > 0 ? (
                <div className={styles['report-codes-value']}>
                  {summary.reports.map(({ reportId, reportCode }, index) => (
                    <Fragment key={reportId}>
                      <TextButton size="tiny" variant="underline" onClick={() => handleViewReportDetail(reportId)}>
                        {reportCode}
                      </TextButton>
                      {index !== summary.reports.length - 1 && (
                        <Text size="medium" weight="semi-bold" element="span" color="kurly">
                          ,
                        </Text>
                      )}
                    </Fragment>
                  ))}
                </div>
              ) : (
                '-'
              )
            }
          />
        </ModalSection>
        <ModalSection className={styles['progress-info']} title="처리 현황">
          <div className={styles['progress-info-section']}>
            <Text size="large" weight="semi-bold">
              입고 진행 상태
            </Text>
            <div className={styles['progress-counts-container']}>
              <ProgressCount
                className={styles['count']}
                title="수량"
                value={progressCount.totalReceivedQty}
                total={progressCount.totalRequestedQty}
                valueColor="success"
              />
              <ProgressCount
                className={styles['count']}
                title="품목"
                value={progressCount.receivedSku}
                total={progressCount.requestedSku}
                valueColor="success"
              />
            </div>
          </div>
          <Divider className={styles['divider']} color="gray100" />
          <div className={styles['progress-info-section']}>
            <Text size="large" weight="semi-bold">
              리포트 진행 상태
            </Text>
            <ProgressBar
              percent={[
                {
                  value: reportProgressCount.totalQty
                    ? ((reportProgressCount.completedQty ?? 0) / reportProgressCount.totalQty) * 100
                    : 0,
                  color: 'success',
                },
                {
                  value: reportProgressCount.totalQty
                    ? ((reportProgressCount.incompleteQty ?? 0) / reportProgressCount.totalQty) * 100
                    : 0,
                  color: 'error',
                },
              ]}
            />
            <div className={styles['progress-bar-counts-container']}>
              <ProgressCount
                className={styles['count']}
                title="전체"
                value={reportProgressCount.totalQty}
                showDashIfZero
              />
              <ProgressCount
                className={styles['count']}
                title="완료"
                value={reportProgressCount.completedQty}
                valueColor="success"
                showDashIfZero
              />
              <ProgressCount
                className={styles['count']}
                title="미완료"
                value={reportProgressCount.incompleteQty}
                valueColor="error"
                showDashIfZero
              />
            </div>
          </div>
        </ModalSection>
        <ModalSection className={styles['items-info']} title="입고 처리 상세">
          <Table className={styles['table']} data={items} options={{ columnDefs }} rowStatusType="NO_DATA" />
        </ModalSection>
      </div>
      {reportDetailModal.isOpen && reportDetailData && (
        <ReceiveReportDetailModal
          size="large"
          open={reportDetailModal.isOpen}
          data={reportDetailData}
          onClose={reportDetailModal.handleCloseModal}
        />
      )}
    </Modal>
  );
};

export default ReceiveStatusDetailModal3PL;
